import { useState } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { PopulationService } from "@services/engagements/population/populationService";

interface PopulationControlProps {
  launchId: number;
  hasSegment: boolean;
  enabled: boolean;
  pct: number;
  controlCount: number;  // people currently held out (CPS holdout for the segment)
  onApplied: () => void;
}

/**
 * Control-group (holdout) policy for the population. The holdout is a property of the
 * population segment, not of any one audience — toggling it here randomly assigns ~pct%
 * of the segment as a control group held out from ad delivery. Totals (overall + per
 * audience) are shown by PopulationStats, sourced from the CPS holdout regardless of how
 * it was populated.
 */
export function PopulationControl({ launchId, hasSegment, enabled: initialEnabled, pct: initialPct, controlCount, onApplied }: PopulationControlProps) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [pct, setPct] = useState(initialPct);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      await new PopulationService().setControlGroup(launchId, enabled, Math.max(1, Math.min(50, pct)));
      onApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update control group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-3 p-3 bg-white mb-3">
      <div className="row g-3">
        {/* Left half — controls */}
        <div className="col-12 col-md-6">
          <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
            Control group
          </div>

          {!hasSegment ? (
            <div className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>
              Add a population segment to enable a control group.
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Form.Check
                  type="switch"
                  id="population-control-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  label="Hold out a random control group"
                  disabled={saving}
                />
                {enabled && (
                  <span className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Holdout</span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={pct}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) setPct(val);
                      }}
                      disabled={saving}
                      className="form-control form-control-sm"
                      style={{ width: "4rem", textAlign: "center" }}
                    />
                    <span className="text-muted small">%</span>
                  </span>
                )}
                <Button variant="primary" size="sm" onClick={apply} disabled={saving} className="d-flex align-items-center gap-1">
                  {saving ? <><Spinner animation="border" size="sm" /> Applying…</> : "Apply"}
                </Button>
              </div>
              <div className="text-muted mt-2" style={{ fontSize: "0.78rem" }}>
                Randomly holds out ~{pct}% of the population from ad delivery as a control group.
              </div>
            </>
          )}
        </div>

        {/* Right half — currently held out */}
        <div className="col-12 col-md-6 border-start d-flex flex-column justify-content-center">
          <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
            Currently held out
          </div>
          <div className="fw-bold" style={{ fontSize: "1.6rem", lineHeight: 1.1 }}>
            {hasSegment ? controlCount.toLocaleString() : "—"}
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" className="mt-2 mb-0 py-2">{error}</Alert>}
    </div>
  );
}
